import { useMemo, useState } from 'react'
import { Box, Button, Heading, HStack, Stack, Text } from '@chakra-ui/react'
import { StatusChip, type StatusVariant } from '../shared/StatusChip'
import { DistrictFields, type DistrictFieldsValue } from '../contacts/DistrictFields'
import { useUpdateContact } from '../../hooks/useContactDetail'
import { extractApiMessage } from '../../lib/apiError'
import type { ContactDetail, ContactDistrict, DistrictChamber } from '../../types/contactDetail'
import type { UpdateContactRequest } from '../../types/contactDetail'

const CHAMBER_LABEL: Record<DistrictChamber, string> = {
  Congressional: 'Congressional',
  StateSenate: 'State Senate',
  StateHouse: 'State House',
}

const FIELD_BY_CHAMBER: Record<DistrictChamber, keyof DistrictFieldsValue> = {
  Congressional: 'locCongressional',
  StateSenate: 'locStateSenate',
  StateHouse: 'locStateHouse',
}

function chip(d: ContactDistrict): { variant: StatusVariant; label: string } {
  if (d.source === 'VoterFile') return { variant: 'info', label: 'From voter file' }
  if (d.source === 'Manual') return { variant: 'info', label: 'Manually entered' }
  if (d.source === 'Derived') return { variant: 'neutral', label: 'From address' }
  return { variant: 'neutral', label: 'Not set' }
}

/**
 * Per-chamber district view + inline edit for a roster-native contact (roster-native-districts
 * §3.6). A voter-linked contact's districts come from the voter file and are read-only. Semantic
 * tokens only; 390px is definition-of-done.
 */
export function DistrictsSection({ contact, canEdit }: { contact: ContactDetail; canEdit: boolean }) {
  const update = useUpdateContact(contact.id)
  const save = update.mutate
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const byChamber = useMemo(() => {
    const m = new Map<DistrictChamber, ContactDistrict>()
    for (const d of contact.districts) m.set(d.chamber, d)
    return m
  }, [contact.districts])

  const original = useMemo<DistrictFieldsValue>(() => ({
    locCongressional: byChamber.get('Congressional')?.value ?? '',
    locStateSenate: byChamber.get('StateSenate')?.value ?? '',
    locStateHouse: byChamber.get('StateHouse')?.value ?? '',
  }), [byChamber])

  const [draft, setDraft] = useState<DistrictFieldsValue>(original)

  const anyEditable = contact.districts.some((d) => d.editable)

  const startEdit = () => { setDraft(original); setError(null); setEditing(true) }
  const cancel = () => { setEditing(false); setError(null) }

  const onSave = () => {
    setError(null)
    // Send ONLY the chambers the organizer changed; "" clears one (and asks for a fresh derivation).
    const req: UpdateContactRequest = {}
    for (const key of Object.keys(FIELD_BY_CHAMBER) as DistrictChamber[]) {
      const field = FIELD_BY_CHAMBER[key]
      if ((draft[field] ?? '') !== (original[field] ?? '')) req[field] = draft[field] ?? ''
    }
    save(req, {
      onSuccess: () => setEditing(false),
      onError: (e) => setError(extractApiMessage(e, 'Could not save districts.')),
    })
  }

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <HStack justify="space-between" mb={4}>
        <Heading size="sm">Districts</Heading>
        {canEdit && anyEditable && !editing && (
          <Button size="sm" variant="ghost" minH="44px" onClick={startEdit}>Edit districts</Button>
        )}
      </HStack>

      {editing ? (
        <Stack gap={4} align="stretch">
          <DistrictFields
            state={contact.state}
            value={draft}
            onChange={(field, next) => setDraft((d) => ({ ...d, [field]: next }))}
          />
          {error && <Text fontSize="sm" color="danger.fg">{error}</Text>}
          <HStack>
            <Button size="sm" colorPalette="brand" minH="44px" onClick={onSave} loading={update.isPending}>Save</Button>
            <Button size="sm" variant="ghost" minH="44px" onClick={cancel}>Cancel</Button>
          </HStack>
        </Stack>
      ) : (
        <Stack gap={3} align="stretch">
          {(Object.keys(CHAMBER_LABEL) as DistrictChamber[]).map((chamber) => {
            const d = byChamber.get(chamber) ?? { chamber, value: null, source: null, editable: false }
            const c = chip(d)
            return (
              <Stack key={chamber} direction={{ base: 'column', sm: 'row' }} justify="space-between"
                     align={{ base: 'start', sm: 'center' }}>
                <Text color="ink.2">{CHAMBER_LABEL[chamber]} · {d.value ?? '—'}</Text>
                <StatusChip variant={c.variant}>{c.label}</StatusChip>
              </Stack>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}
